import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatTableDataSource as MatTableDataSource } from '@angular/material/table';
import { interval, Subscription } from 'rxjs';
import { AbstractWidgetComponent, AppClientConfiguration, WidgetStyle } from '@sdbn/common';
import { ContextChange } from '@sdbn/common';
import { CMovementOrderTableRow, MovementOrderDetails, ServiceTruckOption, TruckGroup } from '../movement-orders.model';
import { MovementOrdersService } from '../movement-orders.service';
import { SelectionModel } from '@angular/cdk/collections';
import { ComponentSelectionStorageService } from '@sdbn/common';

@Component({
  selector: 'app-movement-orders',
  templateUrl: './movement-orders.component.html',
  styleUrls: ['./movement-orders.component.scss']
})
export class MovementOrdersComponent extends AbstractWidgetComponent implements OnInit, OnDestroy {

  private STORAGE_KEY = 'movementorders.settings';

  truckGroupsEnabled = true;
  truckGroups = new Array<TruckGroup>();
  truckGroupsOfSelectedTruck = new Array<TruckGroup>();
  selectedTruckGroup: TruckGroup;
  serviceTruckOptions = new Array<ServiceTruckOption>();
  selectedServiceTruck: ServiceTruckOption;
  movementOrderDetails: MovementOrderDetails;

  refreshEnabled = true;
  refreshInterval = 30000;
  refreshTimer: Subscription;

  serviceTruckSelectionControl = new FormControl(null, [Validators.required]);

  columnsToDisplay: string[] = ['priority', 'referenceDate', 'scope', 'identifier', 'currentStation', 'targetStation', 'transportReference', 'movementStrategy', 'processingStatus'];
  dataSource: MatTableDataSource<CMovementOrderTableRow>;
  selection = new SelectionModel<CMovementOrderTableRow>(false);

  constructor(private movementOrdersService: MovementOrdersService,
    private componentSelectionStorageService: ComponentSelectionStorageService,
    private appClientConfiguration: AppClientConfiguration) {
    super();

    this.movementOrdersService.getTruckGroups().subscribe(truckGroups => {
      this.truckGroups = truckGroups;

      this.movementOrdersService.loadServiceTruckOptions().subscribe(
        options => {
          this.serviceTruckOptions = options;
          if (this.serviceTruckOptions?.length > 0) {
            this.restoreSelectionFromStorage();
            if (this.selectedServiceTruck == null) {
              this.serviceTruckSelectionControl.setValue(this.serviceTruckOptions[0]);
              //this.selectedServiceTruck = this.serviceTruckOptions[0];
            }
          }
        }
      );
    });

    this.serviceTruckSelectionControl.valueChanges.subscribe(
      v => {
        console.log('serviceTruckSelectionControl.valueChanged', v);
        this.selectedServiceTruck = v;
        this.storeSettings();
        this.setTruckGroupsForSelectedServiceTruck();
        this.performReloadOfMovementOrders();
      }
    );

    this.dataSource = new MatTableDataSource(new Array<CMovementOrderTableRow>());
  }

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.refreshTimer) {
      this.refreshTimer.unsubscribe();
      this.refreshTimer = null;
    }
  }

  onContextChange(contextChange: ContextChange) { }

  onSetElementAndContext() {
    this.refreshEnabled = this.widgetReference.refreshEnabled == null ? true : this.widgetReference.refreshEnabled;
    if (this.widgetReference.refreshInterval != null && this.widgetReference.refreshInterval > 0) {
      this.refreshInterval = this.widgetReference.refreshInterval * 1000;
    } else {
      this.refreshInterval = 30 * 1000;
    }
    this.performReloadOfMovementOrders();
  }

  selectTruckGroup(truckGroup: TruckGroup) {
    this.selectedTruckGroup = truckGroup;
    this.performReloadOfMovementOrders();
  }


  onMovementOrderDetailsChanged(): void {
    setTimeout(() => this.performReloadOfMovementOrders(), 300);
  }

  private setTruckGroupsForSelectedServiceTruck() {
    const groups = new Array<TruckGroup>();
    groups.push(...this.truckGroups.filter(tg => tg.trucks.indexOf(this.selectedServiceTruck.truckId) >= 0));
    this.truckGroupsOfSelectedTruck = groups;
    if (this.truckGroupsOfSelectedTruck.length > 0) {
      this.selectedTruckGroup = this.truckGroupsOfSelectedTruck[0];
    }
  }

  private performReloadOfMovementOrders() {
    if (this.refreshTimer) {
      this.refreshTimer.unsubscribe();
    }
    this.loadMovementOrders();
    this.registerRefreshTimer();
  }

  private registerRefreshTimer() {

    if (this.refreshTimer) {
      this.refreshTimer.unsubscribe();
    }

    if (this.appClientConfiguration.autoreload && this.refreshEnabled) {
      this.refreshTimer = interval(this.refreshInterval).subscribe(() => this.loadMovementOrders());
    } else {
      console.log('Autoreload YardMapComponent disabled');
    }
  }

  private loadMovementOrders(): void {
    if (this.selectedTruckGroup != null && this.selectedServiceTruck != null) {
      this.movementOrdersService.loadMovementOrderTableRowsForTruckGroup(this.selectedServiceTruck.truckId, this.selectedTruckGroup.id)
        .subscribe(
          orders => {
            const oldSelection = this.selection?.selected[0]?.id;
            this.selection.clear();
            this.dataSource.data = orders;
            const restoredSelection = this.dataSource.data.find(d => d.id === oldSelection);
            this.toggleSelection(restoredSelection);
          }
        );
    }
  }

  public toggleSelection(movementOrderRow: CMovementOrderTableRow) {
    if (movementOrderRow != null) {
      this.selection.select(movementOrderRow);
    } else {
      this.selection.clear();
    }
    this.loadMovementOrderDetails();
  }

  public getRowStyles(movementOrderRow: CMovementOrderTableRow) {
    const styles: string[] = [];
    if (this.selection.isSelected(movementOrderRow)) {
      styles.push('row-selected');
    } else {
      styles.push('row-deselected');
    }
    return styles;
  }

  private loadMovementOrderDetails() {
    this.movementOrderDetails = null;
    if (this.selection.selected.length > 0) {
      this.movementOrdersService.loadMovementOrderDetails(this.selection.selected[0].id)
        .subscribe(
          details => this.movementOrderDetails = details
        );
    }
  }


  private restoreSelectionFromStorage() {
    const settings = this.componentSelectionStorageService.getWidgetSelection(this.STORAGE_KEY, this.widgetIdentifier);
    if (settings != null && settings.selectedServiceTruck != null) {
      console.log('restore', settings, this.serviceTruckOptions);
      this.serviceTruckOptions.forEach(availableServiceTruckOptions => {
        if (availableServiceTruckOptions.truckId == settings.selectedServiceTruck.truckId) {
          this.serviceTruckSelectionControl.setValue(availableServiceTruckOptions);
        }
      });

    }
  }

  private storeSettings() {
    const settings: any = {
      selectedServiceTruck: this.selectedServiceTruck
    };
    this.componentSelectionStorageService.saveWidgetSelection(this.STORAGE_KEY, this.widgetIdentifier, settings);
  }

  getStyle(): WidgetStyle {
    return WidgetStyle.transparent;
  }
}
